
import React, { useContext, useState } from "react";
import { Table, Input, Button, Space, Tag, Switch, Tooltip, message } from "antd";
import { SearchOutlined, EditOutlined, DeleteOutlined, EnvironmentOutlined, RadiusUprightOutlined } from "@ant-design/icons";
import { WarehouseContext } from './'
import { ApiServices } from "../../services/apiServices";
import { ApiConfig } from "../../services/ApiConfigs";
import { Truck } from "react-feather";

const DataTables = () => {
    const [searchText, setSearchText] = useState("");
    const [filteredData, setFilteredData] = useState([]);


    const { wareHouseData, deleteUser, loader, setSelectWarehouseData, setAddModalOpen, setIsEditMode, getWarehouse, isFranchise } = useContext(WarehouseContext)

    const [radiusToggling, setRadiusToggling] = useState({}) // { [id]: true } when loading

    const handleRadiusToggle = async (record, checked) => {
        setRadiusToggling(prev => ({ ...prev, [record.id]: true }))
        try {
            const res = await ApiServices('put', `${ApiConfig.WAREHOUSE}/${record.id}`, {
                ...record,
                radiusEnabled: checked,
            })
            // ApiServices returns raw JSON body — backend sends { status: 200, data: ... }
            if (res?.status === 200) {
                message.success(checked ? 'Delivery radius coverage enabled' : 'Delivery radius coverage disabled')
                getWarehouse()
            } else {
                message.error(res?.message || 'Failed to update radius setting')
            }
        } catch (e) {
            console.error('[radiusToggle] error:', e)
            message.error('Failed to update radius setting')
        } finally {
            setRadiusToggling(prev => ({ ...prev, [record.id]: false }))
        }
    }

    const handleSearch = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchText(value);
        const filtered = wareHouseData.filter((item) =>
            Object.values(item).some((field) =>
                field.toString().toLowerCase().includes(value)
            )
        );
        setFilteredData(filtered);
    };

    const openMap = (latitude, longitude) => {
        const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
        window.open(url, "_blank");
    };


    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => confirm()}
                    style={{ width: 188, marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button type="primary" onClick={() => confirm()} size="small" icon={<SearchOutlined />}>
                        Search
                    </Button>
                    <Button onClick={clearFilters} size="small">
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) => record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    });

    const getFilters = (data, key) => {
        const uniqueValues = [...new Set(data.map((item) => item[key]))];
        return uniqueValues.map((value) => ({ text: value, value }));
    };

    const columns = [
        {
            title: "Warehouse Name",
            dataIndex: "name",
            key: "name",
            minWidth: 160,
            sorter: (a, b) => a.name.localeCompare(b.name),
            render: (text) => (
                <span style={{ fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>{text || '—'}</span>
            ),
            ...getColumnSearchProps("name"),
        },
        {
            title: "City / State",
            key: "cityState",
            minWidth: 130,
            render: (_, r) => (
                <span style={{ color: '#475569', whiteSpace: 'nowrap' }}>
                    {[r.city, r.state].filter(Boolean).join(', ') || '—'}
                </span>
            ),
        },
        {
            title: "Address",
            dataIndex: "address",
            key: "address",
            minWidth: 180,
            responsive: ['md', 'lg', 'xl'],
            render: (text) => (
                <span style={{ color: '#64748b', fontSize: 13 }}>{text || '—'}</span>
            ),
        },
        {
            title: "Pincode",
            dataIndex: "pincode",
            key: "pincode",
            width: 90,
            responsive: ['sm', 'md', 'lg', 'xl'],
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text || '—'}</span>,
        },
        {
            title: "Contact",
            dataIndex: "contactPhone",
            key: "contactPhone",
            minWidth: 120,
            responsive: ['sm', 'md', 'lg', 'xl'],
            render: (text) => <span style={{ whiteSpace: 'nowrap', color: '#334155' }}>{text || '—'}</span>,
            ...getColumnSearchProps("contactPhone"),
        },
        {
            title: "Manager",
            dataIndex: "manager",
            key: "manager",
            minWidth: 120,
            responsive: ['md', 'lg', 'xl'],
            filters: getFilters(wareHouseData, "manager"),
            onFilter: (value, record) => record.manager === value,
            render: (text) => <span style={{ whiteSpace: 'nowrap' }}>{text || '—'}</span>,
        },
        {
            title: "Coverage Radius",
            key: "deliveryRadiusKm",
            minWidth: 160,
            render: (_, record) => {
                const radius = record.deliveryRadiusKm || 10
                const enabled = !!record.radiusEnabled
                const loading = !!radiusToggling[record.id]
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap' }}>
                        {!isFranchise ? (
                            <Tooltip title={enabled ? 'Radius filtering ON — only assign orders within radius' : 'Radius filtering OFF — accept all orders regardless of distance'}>
                                <Switch
                                    size="small"
                                    checked={enabled}
                                    loading={loading}
                                    onChange={(checked) => handleRadiusToggle(record, checked)}
                                    style={{ flexShrink: 0 }}
                                />
                            </Tooltip>
                        ) : null}
                        <Tag
                            color={enabled ? 'blue' : 'default'}
                            style={{ margin: 0, whiteSpace: 'nowrap', opacity: enabled ? 1 : 0.5 }}
                        >
                            {radius} km
                        </Tag>
                    </div>
                )
            },
        },
        {
            title: "Actions",
            key: "actions",
            width: isFranchise ? 70 : 110,
            fixed: 'right',
            render: (_, record) => (
                <Space size={0}>
                    {!isFranchise && (
                        <>
                            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => editWarehouse(record)} />
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => deleteUser(record)} />
                        </>
                    )}
                    <Button
                        type="text"
                        size="small"
                        icon={<EnvironmentOutlined style={{ color: '#2563eb' }} />}
                        onClick={() => openMap(record.latitude, record.longitude)}
                    />
                </Space>
            ),
        },
    ];

    // Edit warehouse function
    const editWarehouse = (record) => {
        console.log("Edit Warehouse:", record);
        setIsEditMode(true)
        setSelectWarehouseData(record);
        setAddModalOpen(true)
    };

    return (
        <div>
            <Input
                placeholder="Search Warehouses..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={handleSearch}
                style={{ marginBottom: 16, width: '100%', maxWidth: 360 }}
                allowClear
            />

            <Table
                columns={columns}
                dataSource={searchText ? filteredData : wareHouseData}
                pagination={{ pageSize: 5, showSizeChanger: false }}
                rowKey="id"
                loading={loader}
                scroll={{ x: 900 }}
            />
        </div>
    );
};

export default DataTables;
